/**
 * MapaRepartidor.jsx
 * 
 * Componente de mapa para la página del repartidor
 * Muestra la posición actual del repartidor usando geolocalización del navegador
 * 
 * Qué hace:
 * - Muestra la ubicación actual del repartidor
 * - Centra el mapa en la posición del repartidor
 * - Muestra un marcador personalizado
 * 
 * Qué recibe (props):
 * - posicionActual (Object): {lat, lng} de la posición actual del repartidor
 * 
 * Qué devuelve:
 * - JSX con el componente del mapa
 */

import React, { useEffect } from 'react'
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet'
import L from 'leaflet'

// Importar iconos de Leaflet
import 'leaflet/dist/leaflet.css'

// Fix para los iconos de Leaflet en React
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
})

/**
 * Componente que centra el mapa en la posición del repartidor
 */
function CentrarEnPosicion({ posicionActual }) {
  const map = useMap()

  useEffect(() => {
    if (!posicionActual) return

    // Centrar el mapa en la posición actual
    map.panTo(
      [posicionActual.lat, posicionActual.lng],
      { animate: true, duration: 0.6, easeLinearity: 0.25 }
    )

    // Ajustar zoom para una buena vista
    if (map.getZoom() < 17) {
      map.setZoom(17, { animate: true, duration: 0.5 })
    }
  }, [posicionActual, map])

  return null
}

/**
 * Componente principal del mapa del repartidor
 */
function MapaRepartidor({ 
  posicionActual = null
}) {
  // Coordenadas por defecto (Buenos Aires, Argentina)
  const centroPorDefecto = [-34.6037, -58.3816]
  const zoomPorDefecto = 15

  // Crear icono personalizado para el repartidor
  const iconoRepartidor = L.divIcon({
    className: 'marcador-repartidor-actual',
    html: '<div style="background-color: #10b981; width: 40px; height: 40px; border-radius: 50%; border: 4px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; color: white; font-size: 24px;">📍</div>',
    iconSize: [40, 40],
    iconAnchor: [20, 20]
  })

  // Calcular centro del mapa
  let centro = centroPorDefecto
  if (posicionActual) {
    centro = [posicionActual.lat, posicionActual.lng]
  }

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <MapContainer
        center={centro}
        zoom={posicionActual ? 17 : zoomPorDefecto}
        style={{ height: '100%', width: '100%' }}
      >
        {/* Capa de tiles (mapa base) */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Centrar en la posición actual */}
        {posicionActual && (
          <CentrarEnPosicion posicionActual={posicionActual} />
        )}

        {/* Marcador de la posición actual */}
        {posicionActual && (
          <Marker position={[posicionActual.lat, posicionActual.lng]} icon={iconoRepartidor}>
            <Popup>
              <strong>Tu Ubicación</strong>
              <p>Posición actual</p>
              <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.5rem' }}>
                Lat: {posicionActual.lat.toFixed(6)}<br />
                Lng: {posicionActual.lng.toFixed(6)}
              </p>
            </Popup>
          </Marker>
        )}
      </MapContainer>

      {/* Estilos CSS para la animación del pulso */}
      <style>{`
        @keyframes pulse {
          0% {
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
          }
          70% {
            box-shadow: 0 0 0 10px rgba(16, 185, 129, 0);
          }
          100% {
            box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
          }
        }
        .marcador-repartidor-actual > div {
          animation: pulse 2s infinite;
        }
      `}</style>
    </div>
  )
}

export default MapaRepartidor

